import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Icon } from 'react-inv-forms';
import EditableCell from '../EditableCell';
import TableActionButtons from '../TableActionButtons';
// import { SingleColumn } from '../../../../redux/types';
import addRed from '../../../../images/addRed.svg';
import { CustomTable, FabButton, Label } from '../../../../styles/sharedUIElemStyles';
import { inlineTableColumns } from '../../../../settings/tableColumns';
import DataDestructuring from '../../../../helpers/DataDestructuring';

const { addKey, numberData, createReadOnlyColumns } = DataDestructuring;

interface Props {
  value: Form.InlineTableFormValue;
  updateFormData: (data: any) => void;
  index: string;
  defaultValue: Form.InlineTableFormValue;
  readOnly: boolean;
  template: any;
}
/**
 * Komponent tabeli z wartościami, które uzytkownik samodzielnie wprowadza
 *
 */
const InlineTable = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const [isRecordNew, setNewRecord] = useState(false);

  const isEditing = (record: any): boolean => record.key === editingKey;

  const edit = (record: any): void => {
    form.setFieldsValue({
      desc: '',
      ...record,
    });
    setEditingKey(record.key);
  };
  const addNewRecord = (): void => {
    setNewRecord(true);
    const valueWithNewRecord = addKey(props.value.data);
    const newRecord = {
      id: '',
      desc: '',
      key: props.value.data.length,
    };
    valueWithNewRecord.push(newRecord);
    const updatedData = { ...props.value, data: valueWithNewRecord };
    props.updateFormData(updatedData);
    edit(newRecord);
  };
  const cancelNewRecord = (key: string): void => {
    deleteRecord(key);
    setEditingKey('');
    setNewRecord(false);
  };
  const deleteRecord = (id: string): void => {
    const dataWithKey = addKey(props.value.data);
    const updatedValue = {
      ...props.value,
      data: dataWithKey.filter(item => item.key !== id),
    };
    props.updateFormData(updatedValue);
  };

  const confirm = async (record: any): Promise<void> => {
    const modifiedCells = await form.validateFields();
    const row = { ...record, ...modifiedCells };
    const newData = addKey(props.value.data);
    const index = newData.findIndex(item => record.key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });
      const updatedValue = { ...props.value, data: newData };
      props.updateFormData(updatedValue);
      setEditingKey('');
      if (isRecordNew) {
        setNewRecord(false);
      }
    } else {
      setEditingKey('');
    }
  };
  const actionColumns: SingleColumn[] = [...inlineTableColumns];
  actionColumns.push({
    title: '',
    dataIndex: 'actions',
    width: '10%',
    sorter: false,
    // eslint-disable-next-line react/display-name
    render: (_: any, record: any): React.ReactElement => {
      const editable = isEditing(record);
      return (
        <TableActionButtons
          editable={editable}
          onChangeConfirmation={confirm.bind(null, record)}
          onChangeCancel={isRecordNew ? cancelNewRecord.bind(null, record.key) : setEditingKey.bind(null, '')}
          onEdit={edit.bind(null, record)}
          disabled={editingKey !== ''}
          onDelete={deleteRecord.bind(null, record.key)}
        />
      );
    },
  });
  const mergedColumns = actionColumns.map((col: SingleColumn) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: SingleColumn): SingleColumn => ({
        record,
        inputType: col.type,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        sorter: false,
      }),
    };
  });
  const dataWithKey = addKey(props.value.data);
  const defaultDataWithKey = addKey(props.defaultValue.data);

  return (
    <>
      {!props.readOnly ? <Label>{t('BEFORE_CHANGE')}</Label> : null}
      <CustomTable
        dataSource={props.readOnly ? numberData(dataWithKey) : numberData(defaultDataWithKey)}
        columns={!props.readOnly ? createReadOnlyColumns(inlineTableColumns) : inlineTableColumns}
        rowClassName='editable-row'
        pagination={false}
        locale={{ emptyText: t('NO_DATA') }}
        className={!props.readOnly ? 'table__readOnly' : ''}
      />
      {!props.readOnly ? (
        <Form form={form} component={false}>
          <CustomTable
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            bordered
            dataSource={numberData(dataWithKey)}
            columns={mergedColumns}
            rowClassName='editable-row'
            pagination={false}
            locale={{ emptyText: t('NO_DATA') }}
          />
          <FabButton
            onClick={addNewRecord}
            disabled={editingKey !== ''}
            data-cy={`btnSubjectCardAddInlineTable_${props.value.dataIndex}`}
          >
            <Icon path={addRed} />
          </FabButton>
        </Form>
      ) : null}
    </>
  );
};

export default InlineTable;
